// Utilitário para integração com Google Calendar API

export interface GoogleCalendarEvent {
  id: string;
  summary: string;
  description: string;
  start: {
    dateTime: string;
    timeZone: string;
  };
  end: {
    dateTime: string;
    timeZone: string;
  };
  location: string;
  attendees?: Array<{
    email: string;
    displayName?: string;
  }>;
}

export interface BookingData {
  date: string;
  time: string;
  serviceName: string;
  customerName: string;
  customerEmail?: string;
  customerPhone: string;
  companyName: string;
  companyAddress: string;
  serviceDuration: number;
  serviceDescription?: string;
}

export interface UnavailabilityEventData {
  title: string;
  description: string;
  startDateTime: string;
  endDateTime: string;
  isAllDay: boolean;
}

function throwGoogleCalendarHttpError(
  errorData: { error?: { message?: string } },
  statusText: string
): never {
  const raw = String(errorData?.error?.message || statusText || "");
  if (/has not been used|is disabled|calendar-json\.googleapis/i.test(raw)) {
    throw new Error("google_calendar_api_disabled");
  }
  throw new Error("google_calendar_request_failed");
}

// Criar evento no Google Calendar
export const createGoogleCalendarEvent = async (
  accessToken: string,
  bookingData: BookingData
): Promise<GoogleCalendarEvent> => {
  try {
    const startDateTime = new Date(`${bookingData.date}T${bookingData.time}`);
    const endDateTime = new Date(startDateTime.getTime() + bookingData.serviceDuration * 60000);

    const event = {
      summary: `${bookingData.serviceName} - ${bookingData.customerName}`,
      description: `${bookingData.serviceDescription || "Agendamento"}\n\nCliente: ${bookingData.customerName}\nTelefone: ${bookingData.customerPhone}${bookingData.customerEmail ? `\nEmail: ${bookingData.customerEmail}` : ""}\n\nEmpresa: ${bookingData.companyName}`,
      start: {
        dateTime: startDateTime.toISOString(),
        timeZone: "America/Sao_Paulo",
      },
      end: {
        dateTime: endDateTime.toISOString(),
        timeZone: "America/Sao_Paulo",
      },
      location: bookingData.companyAddress,
      attendees: bookingData.customerEmail ? [
        {
          email: bookingData.customerEmail,
          displayName: bookingData.customerName,
        },
      ] : undefined,
      reminders: {
        useDefault: false,
        overrides: [
          { method: "email", minutes: 24 * 60 }, // 1 dia antes
          { method: "popup", minutes: 60 }, // 1 hora antes
        ],
      },
    };

    const createUrl = new URL(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events"
    );
    if (bookingData.customerEmail) {
      createUrl.searchParams.set("sendUpdates", "all");
    }
    const response = await fetch(createUrl.toString(), {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throwGoogleCalendarHttpError(errorData, response.statusText);
    }

    const createdEvent = await response.json();
    return createdEvent;
  } catch (error) {
    console.error("Erro ao criar evento no Google Calendar:", error);
    throw error;
  }
};

// Atualizar evento no Google Calendar
export const updateGoogleCalendarEvent = async (
  accessToken: string,
  eventId: string,
  bookingData: BookingData
): Promise<GoogleCalendarEvent> => {
  try {
    const startDateTime = new Date(`${bookingData.date}T${bookingData.time}`);
    const endDateTime = new Date(startDateTime.getTime() + bookingData.serviceDuration * 60000);

    const event = {
      summary: `${bookingData.serviceName} - ${bookingData.customerName}`,
      description: `${bookingData.serviceDescription || "Agendamento"}\n\nCliente: ${bookingData.customerName}\nTelefone: ${bookingData.customerPhone}${bookingData.customerEmail ? `\nEmail: ${bookingData.customerEmail}` : ""}\n\nEmpresa: ${bookingData.companyName}`,
      start: {
        dateTime: startDateTime.toISOString(),
        timeZone: "America/Sao_Paulo",
      },
      end: {
        dateTime: endDateTime.toISOString(),
        timeZone: "America/Sao_Paulo",
      },
      location: bookingData.companyAddress,
      attendees: bookingData.customerEmail ? [
        {
          email: bookingData.customerEmail,
          displayName: bookingData.customerName,
        },
      ] : undefined,
      reminders: {
        useDefault: false,
        overrides: [
          { method: "email", minutes: 24 * 60 }, // 1 dia antes
          { method: "popup", minutes: 60 }, // 1 hora antes
        ],
      },
    };

    const updateUrl = new URL(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`
    );
    if (bookingData.customerEmail) {
      updateUrl.searchParams.set("sendUpdates", "all");
    }
    const response = await fetch(updateUrl.toString(), {
      method: "PUT",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(event),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao atualizar evento: ${errorData.error?.message || response.statusText}`
      );
    }

    const updatedEvent = await response.json();
    return updatedEvent;
  } catch (error) {
    console.error("Erro ao atualizar evento no Google Calendar:", error);
    throw error;
  }
};

// Cancelar evento no Google Calendar
export const cancelGoogleCalendarEvent = async (
  accessToken: string,
  eventId: string
): Promise<void> => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
      {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao cancelar evento: ${errorData.error?.message || response.statusText}`
      );
    }
  } catch (error) {
    console.error("Erro ao cancelar evento no Google Calendar:", error);
    throw error;
  }
};

// Obter evento do Google Calendar
export const getGoogleCalendarEvent = async (
  accessToken: string,
  eventId: string
): Promise<GoogleCalendarEvent | null> => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/calendar/v3/calendars/primary/events/${eventId}`,
      {
        headers: {
          "Authorization": `Bearer ${accessToken}`,
        },
      }
    );

    if (!response.ok) {
      if (response.status === 404) {
        return null; // Evento não encontrado
      }
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao obter evento: ${errorData.error?.message || response.statusText}`
      );
    }

    const event = await response.json();
    return event;
  } catch (error) {
    console.error("Erro ao obter evento do Google Calendar:", error);
    throw error;
  }
};

// Buscar próximos eventos do Google Calendar do usuário
export const getUpcomingGoogleCalendarEvents = async (
  accessToken: string,
  maxResults: number = 10
): Promise<GoogleCalendarEvent[]> => {
  try {
    console.log("📅 Buscando próximos eventos do Google Calendar...");
    
    // Data atual no formato ISO
    const timeMin = new Date().toISOString();
    
    // Construir URL da API com parâmetros
    const url = new URL("https://www.googleapis.com/calendar/v3/calendars/primary/events");
    url.searchParams.append("timeMin", timeMin);
    url.searchParams.append("maxResults", maxResults.toString());
    url.searchParams.append("singleEvents", "true");
    url.searchParams.append("orderBy", "startTime");
    
    console.log("🌐 Consultando Google Calendar API:", url.toString());
    
    const response = await fetch(url.toString(), {
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throwGoogleCalendarHttpError(errorData, response.statusText);
    }

    const data = await response.json();
    console.log("📅 Eventos encontrados:", data.items?.length || 0);
    
    // Retornar apenas os eventos (items)
    return data.items || [];
  } catch (error) {
    console.error("❌ Erro ao buscar eventos do Google Calendar:", error);
    throw error;
  }
};

// Buscar eventos de hoje do Google Calendar
export const getTodayGoogleCalendarEvents = async (
  accessToken: string
): Promise<GoogleCalendarEvent[]> => {
  try {
    console.log("📅 Buscando eventos de hoje do Google Calendar...");
    
    // Início do dia atual
    const today = new Date();
    const timeMin = new Date(today.getFullYear(), today.getMonth(), today.getDate()).toISOString();
    
    // Final do dia atual
    const timeMax = new Date(today.getFullYear(), today.getMonth(), today.getDate() + 1).toISOString();
    
    const url = new URL("https://www.googleapis.com/calendar/v3/calendars/primary/events");
    url.searchParams.append("timeMin", timeMin);
    url.searchParams.append("timeMax", timeMax);
    url.searchParams.append("singleEvents", "true");
    url.searchParams.append("orderBy", "startTime");
    
    console.log("🌐 Consultando eventos de hoje:", url.toString());
    
    const response = await fetch(url.toString(), {
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao buscar eventos de hoje: ${errorData.error?.message || response.statusText}`
      );
    }

    const data = await response.json();
    console.log("📅 Eventos de hoje encontrados:", data.items?.length || 0);
    
    return data.items || [];
  } catch (error) {
    console.error("❌ Erro ao buscar eventos de hoje:", error);
    throw error;
  }
};

// Validar token do Google
export const validateGoogleToken = async (accessToken: string): Promise<boolean> => {
  try {
    console.log("🔍 [VALIDATE_TOKEN] Iniciando validação do token Google...");
    console.log("🔍 [VALIDATE_TOKEN] Token (primeiros 20 chars):", accessToken?.substring(0, 20) + "...");
    
    // Verificar se o token não está vazio ou inválido
    if (!accessToken || accessToken.trim() === '') {
      console.warn("❌ [VALIDATE_TOKEN] Token do Google vazio ou inválido");
      return false;
    }

    // Access tokens OAuth2 do Google costumam começar com "ya29." — não rejeitar.
    // Refresh tokens costumam ser "1//…".
    if (accessToken.startsWith("1//")) {
      return false;
    }

    const tokenInfoUrl = `https://www.googleapis.com/oauth2/v1/tokeninfo?access_token=${encodeURIComponent(accessToken)}`;
    console.log("🌐 [VALIDATE_TOKEN] Consultando Google Token Info API...");
    
    const response = await fetch(tokenInfoUrl);
    
    console.log("📊 [VALIDATE_TOKEN] Resposta da API:", {
      status: response.status,
      statusText: response.statusText,
      ok: response.ok,
      headers: Object.fromEntries(response.headers.entries())
    });
    
    if (!response.ok) {
      let errorDetails = "";
      try {
        const errorData = await response.json();
        errorDetails = JSON.stringify(errorData);
        console.error("❌ [VALIDATE_TOKEN] Detalhes do erro da API:", errorData);
      } catch (parseError) {
        const errorText = await response.text();
        errorDetails = errorText;
        console.error("❌ [VALIDATE_TOKEN] Erro ao fazer parse da resposta:", errorText);
      }
      
      console.warn(`❌ [VALIDATE_TOKEN] Token do Google inválido: ${response.status} ${response.statusText}`, {
        errorDetails,
        tokenPreview: accessToken.substring(0, 20) + "...",
        url: tokenInfoUrl
      });
      return false;
    }
    
    const tokenInfo = await response.json();
    console.log("✅ [VALIDATE_TOKEN] Token válido! Informações:", {
      audience: tokenInfo.audience,
      scope: tokenInfo.scope,
      expires_in: tokenInfo.expires_in,
      email: tokenInfo.email
    });
    
    return true;
  } catch (error) {
    console.error("❌ [VALIDATE_TOKEN] Erro ao validar token Google:", {
      error: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
      tokenPreview: accessToken?.substring(0, 20) + "..."
    });
    return false;
  }
};

// Criar evento de indisponibilidade no Google Calendar
export const createUnavailabilityEvent = async (
  accessToken: string,
  eventData: UnavailabilityEventData
): Promise<GoogleCalendarEvent> => {
  try {
    const event = {
      summary: eventData.title,
      description: eventData.description,
      start: eventData.isAllDay 
        ? { date: eventData.startDateTime.split('T')[0] }
        : {
            dateTime: eventData.startDateTime,
            timeZone: "America/Sao_Paulo",
          },
      end: eventData.isAllDay
        ? { date: eventData.endDateTime.split('T')[0] }
        : {
            dateTime: eventData.endDateTime,
            timeZone: "America/Sao_Paulo",
          },
      transparency: "opaque", // Marca como ocupado
      visibility: "private", // Evento privado
      colorId: "11", // Cor vermelha para indicar indisponibilidade
    };

    const response = await fetch(
      "https://www.googleapis.com/calendar/v3/calendars/primary/events",
      {
        method: "POST",
        headers: {
          "Authorization": `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(event),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao criar evento de indisponibilidade: ${errorData.error?.message || response.statusText}`
      );
    }

    const createdEvent = await response.json();
    return createdEvent;
  } catch (error) {
    console.error("Erro ao criar evento de indisponibilidade:", error);
    throw error;
  }
};

// Buscar eventos de indisponibilidade por data
export const getUnavailabilityEventsByDate = async (
  accessToken: string,
  date: string
): Promise<GoogleCalendarEvent[]> => {
  try {
    // Início do dia
    const timeMin = new Date(`${date}T00:00:00`).toISOString();
    // Final do dia
    const timeMax = new Date(`${date}T23:59:59`).toISOString();
    
    const url = new URL("https://www.googleapis.com/calendar/v3/calendars/primary/events");
    url.searchParams.append("timeMin", timeMin);
    url.searchParams.append("timeMax", timeMax);
    url.searchParams.append("singleEvents", "true");
    url.searchParams.append("q", "Atendimento Indisponível"); // Buscar apenas eventos de indisponibilidade
    
    const response = await fetch(url.toString(), {
      headers: {
        "Authorization": `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        `Erro ao buscar eventos de indisponibilidade: ${errorData.error?.message || response.statusText}`
      );
    }

    const data = await response.json();
    return data.items || [];
  } catch (error) {
    console.error("Erro ao buscar eventos de indisponibilidade:", error);
    throw error;
  }
};

/**
 * Formata dados de agendamento para exibição
 * @param bookingData Dados do agendamento
 * @returns Objeto formatado para exibição
 */
export const formatBookingForDisplay = (bookingData: BookingData) => {
  const startDateTime = new Date(`${bookingData.date}T${bookingData.time}`);
  const endDateTime = new Date(
    startDateTime.getTime() + bookingData.serviceDuration * 60 * 1000
  );

  return {
    service: bookingData.serviceName,
    customer: bookingData.customerName,
    date: startDateTime.toLocaleDateString("pt-BR"),
    startTime: startDateTime.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    endTime: endDateTime.toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    duration: `${bookingData.serviceDuration} min`,
    location: bookingData.companyAddress,
  };
};
